import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Highcharts from 'highcharts';
import { LOG } from '../../common/util/log';
import { ChartBinding } from '../chart-interactive/chart-controller.directive';

// Modified from https://github.com/highcharts/highcharts-angular/blob/master/highcharts-angular/src/lib/highcharts-chart.component.ts
// See https://github.com/highcharts/highcharts-angular/blob/master/LICENSE

export enum HighchartsSizeStrategy {
  NONE,
  FIT_CONTAINER,
  FIT_CONTAINER_HEIGHT,
}

@Directive({
  selector: '[highcharts-chart]',
  standalone: true,
})
export class HighchartsDirective implements OnDestroy, OnChanges {
  @Input({ required: false }) public constructorType?: 'chart' | 'stockChart' | 'mapChart';
  @Input({ required: false }) public callbackFunction?: Highcharts.ChartCallbackFunction;
  @Input({ required: false }) public oneToOne?: boolean;
  @Input({ required: false }) public runOutsideAngular?: boolean;
  @Input({ required: true }) public options!: Highcharts.Options;

  @Input({ required: false }) public sizeStrategy?: HighchartsSizeStrategy | undefined;
  @Input({ required: false }) public Highcharts: typeof Highcharts = Highcharts;

  @Output() public chartInstance = new EventEmitter<Highcharts.Chart>();

  private readonly chart: WritableSignal<Highcharts.Chart | null> = signal(null);
  private readonly observer: ResizeObserver;

  private get containerElement(): Element {
    return this.element.nativeElement;
  }

  private readonly element: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly zone: NgZone = inject(NgZone);

  constructor(@Optional() chartBinding: ChartBinding) {
    this.observer = new ResizeObserver(this.resizeObserverCallback);

    if (chartBinding) {
      if (chartBinding.chart && chartBinding.chart !== this.chart) {
        LOG.warn(`Chart binding signal was overwritten.`);
      }

      chartBinding.chart = this.chart;
    }

    this.chartInstance.pipe(takeUntilDestroyed()).subscribe(chart => {
      chartBinding?.chart$.next(chart);
    })
  }

  public ngOnChanges({ options, sizeStrategy }: SimpleChanges): void {
    if (sizeStrategy) {
      switch (this.sizeStrategy) {
        case HighchartsSizeStrategy.FIT_CONTAINER:
        case HighchartsSizeStrategy.FIT_CONTAINER_HEIGHT:
          this.observer.observe(this.containerElement);
          break;

        case undefined:
        case HighchartsSizeStrategy.NONE:
        default:
          this.observer.disconnect();
      }
    }

    if (options) {
      this.wrappedUpdateOrCreateChart();
    }
  }

  public ngOnDestroy(): void {
    if (this.chart()) {
      this.chart().destroy();
      this.chart.set(null);

      this.chartInstance.emit(this.chart());
    }

    this.chartInstance.complete();
  }

  public update(): void {
    this.wrappedUpdateOrCreateChart();
  }

  private wrappedUpdateOrCreateChart(): void {
    if (this.runOutsideAngular) {
      this.zone.runOutsideAngular(() => {
        this.updateOrCreateChart();
      });
    } else {
      this.updateOrCreateChart();
    }
  }

  private updateOrCreateChart(): void {
    if (this.chart() && this.chart().update) {
      this.chart().update(this.options, true, this.oneToOne || false);
    } else {
      const constructorType = this.constructorType;
      this.chart.set(
        (this.Highcharts as any)[constructorType || 'chart'](
          this.containerElement,
          this.options,
          this.callbackFunction || null
        )
      );

      this.chartInstance.emit(this.chart());
    }
  }

  private readonly resizeObserverCallback = (entries: ResizeObserverEntry[]): void => {
    const elementRect = entries[0].contentRect;

    if (elementRect.width === 0 || elementRect.height === 0) {
      return;
    }

    switch (this.sizeStrategy) {
      case HighchartsSizeStrategy.FIT_CONTAINER:
        this.chart()?.setSize(elementRect.width, elementRect.height, { duration: 0 });
        break;

      case HighchartsSizeStrategy.FIT_CONTAINER_HEIGHT:
        this.chart()?.setSize(null, elementRect.height, { duration: 0 });
        break;
    }

    this.chart()?.reflow();
  };
}
